import * as React from "react";
import { useEffect, useRef, useState } from "react";
import { app, dialog, UrlDialogInfo } from "@microsoft/teams-js";
import { Flex, FlexItem, Loader, Segment, Text, ProviderConsumer as FluentUIThemeConsumer, Menu, tabListBehavior, CalendarIcon, Table, Pill, FilterIcon, Tooltip } from '@fluentui/react-northstar';
import { getMeetingDataList, getRating, getRatingList, getRealizedUsageList, getRequests, getUsage, getUsageList } from "../../api/metrics-api";
import Data from "../../constants/data";
import BarChart from "../chart/charts/bar";
import DoughnutChart from "../chart/charts/doughnut";
import BarHorizontalChart from "../chart/charts/bar-horizontal";
import Pagination from "../pagination/pagination";
import statusQueueConstants from "../../constants/status-queue";
import "./dashboards.scss";

enum DashboardTypes {
    general = "general",
    team = "team",
}

enum ChartTypes {
    overview = 0,
    overviewLabel = "Visão geral",
    requests = 1,
    requestsLabel = "Novas solicitações",
    usage = 2,
    usageLabel = "Perfil de uso",
    teamUsage = 3,
    teamUsageLabel = "Equipe",
    rating = 4,
    ratingLabel = "Avaliações",
    teamRating = 5,
    ratingTeamLabel = "Equipe",
    realizedRequests = 6,
    realizedRequestsLabel = "Solicitações atendidas",
    requestsStatus = 7,
    requestsStatusLabel = "Solicitações",
    unitChart = 8,
    unitLabel = "Unidade",
}

interface ChartFilters {
    chartType: ChartTypes;
    value: string;
    additional?: string;
    index?: number | number[];
}

const MemDoughnutChart = React.memo(DoughnutChart, (prevProps, nextProps) => {
    return (JSON.stringify(prevProps.data) === JSON.stringify(nextProps.data) && prevProps.activeSegmentIndex === nextProps.activeSegmentIndex);
});

const MemBarChart = React.memo(BarChart, (prevProps, nextProps) => {
    return (JSON.stringify(prevProps.data) === JSON.stringify(nextProps.data) && prevProps.activeSegmentIndex === nextProps.activeSegmentIndex);
});

const MemBarHorizontalChart = React.memo(BarHorizontalChart, (prevProps, nextProps) => {
    return (JSON.stringify(prevProps.data) === JSON.stringify(nextProps.data) && prevProps.activeSegmentIndex === nextProps.activeSegmentIndex);
});

const MemBarHorizontalRatingChart = React.memo(BarHorizontalChart, (prevProps, nextProps) => {
    return (JSON.stringify(prevProps.data) === JSON.stringify(nextProps.data) && prevProps.activeSegmentIndex === nextProps.activeSegmentIndex);
});

const MemBarHorizontalRatingTeamChart = React.memo(BarHorizontalChart, (prevProps, nextProps) => {
    return (JSON.stringify(prevProps.data) === JSON.stringify(nextProps.data) && prevProps.activeSegmentIndex === nextProps.activeSegmentIndex);
});

const Dashboards = () => {
    const [loading, setLoading] = useState<boolean>(true);
    const [loadingCharts, setLoadingCharts] = useState<boolean>(false);
    const [menuIndex, setMenuIndex] = useState<number>(0);
    const [requests, setRequests] = useState<any>(null);
    const [realizedRequests, setRealizedRequests] = useState<any>(null);
    const [usage, setUsage] = useState<any>(null);
    const [usageList, setUsageList] = useState<any>(null);
    const [usageListCount, setUsageListCount] = useState<any>(null);
    const [usageListPageNumber, setUsageListPageNumber] = useState<any>(null);
    const [realizedUsageList, setRealizedUsageList] = useState<any>(null);
    const [realizedUsageListCount, setRealizedUsageListCount] = useState<any>(null);
    const [realizedUsageListPageNumber, setRealizedUsageListPageNumber] = useState<any>(null);
    const [meetingDataList, setMeetingDataList] = useState<any>(null);
    const [originalMeetingDataList, setOriginalMeetingDataList] = useState<any>([]);
    const [rating, setRating] = useState<any>(null);
    const [ratingList, setRatingList] = useState<any>(null);
    const [ratingListCount, setRatingListCount] = useState<any>(null);
    const [ratingListPageNumber, setRatingListPageNumber] = useState<number>(0);


    const [activeOverviewIndex, setOverviewIndex] = useState<number[] | null>(null);
    const [activeRequestsIndex, setActiveRequestsIndex] = useState<number | null>(null);
    const [activeRequestsStatusIndex, setActiveRequestsStatusIndex] = useState<number[] | null>(null);
    const [activeUsageIndex, setActiveUsageIndex] = useState<number | null>(null);
    const [activeTeamUsageIndex, setActiveTeamUsageIndex] = useState<number[] | null>(null);
    const [activeRealizedRequestsIndex, setActiveRealizedRequestsIndex] = useState<number | null>(null);
    const [activeRatingIndex, setActiveRatingIndex] = useState<number | null>(null);
    const [activeTeamRatingIndex, setActiveTeamRatingIndex] = useState<number[] | null>(null);

    const [chartFilters, setChartFilters] = useState<ChartFilters | null>(null);
    const [unitFilter, setUnitFilter] = useState<string>();
    const [unitCategories, setUnitCategories] = useState<any>();
    const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

    const [theme, setTheme] = useState<string>();

    const isMountedGeneralView = useRef(false);
    const isMountedRequests = useRef(false);
    const isMountedRequestsStatus = useRef(false);
    const isMountedUsage = useRef(false);
    const isMountedTeamUsage = useRef(false);
    const isMountedRealizedRequests = useRef(false);
    const isMountedRealizedUsage = useRef(false);
    const isMountedTeamRealizedUsage = useRef(false);
    const isMountedRating = useRef(false);
    const isMountedTeamRating = useRef(false);

    const chartRef = useRef(null);

    const searchParams = new URLSearchParams(window.location.search);
    const type = searchParams.get("type");

    const requestsColors = ["#27438C", "#4f6bed"];
    const requestsStatusColors = ["#ebc05e", "#467db0", "#92b882", "#fabbc3"];
    const requestsStatusColorsReversed = ["#fabbc3", "#92b882", "#467db0", "#ebc05e"];
    const realizedRequestsColors = ["#4f6bed"];
    const usageColors = ["#9470bd", "#4f6bed", "#27438C"];
    const teamUsageColors = ["#9470bd", "#4f6bed", "#27438C"];
    const ratingColors = ["#a0c15a", "#add633", "#ffd934", "#ffb234", "#ff8c5a"];

    useEffect(() => {
        app.initialize();
        app.getContext().then((context: app.Context) => {
            setTheme(context.app.theme);
            init();
        });
    }, []);

    useEffect(() => {
        if (isMountedGeneralView.current) {
            setUsageListPageNumber(0);

            if (activeOverviewIndex != null && (activeOverviewIndex[0] != null && activeOverviewIndex[1] != null)) {
                chartRef.current = ChartTypes.overview;
                // Requests
                if (activeOverviewIndex[1] === 0) {
                    setChartFilters({
                        chartType: ChartTypes.requests,
                        value: formatDateByMonth(type === DashboardTypes.general ? requests?.requestMetrics[activeOverviewIndex[0]]?.dateString : requests?.requestTeamMetrics[activeOverviewIndex[0]]?.dateString) ?? null,
                        index: [activeOverviewIndex[0], activeOverviewIndex[1]]
                    });
                }

                // Realized Requests
                if (activeOverviewIndex[1] === 1) {
                    setChartFilters({
                        chartType: ChartTypes.realizedRequests,
                        value: formatDateByMonth(type === DashboardTypes.general ? requests?.requestRealizedMetrics[activeOverviewIndex[0]]?.dateString : requests?.requestRealizedTeamMetrics[activeOverviewIndex[0]]?.dateString) ?? null,
                        index: [activeOverviewIndex[0], activeOverviewIndex[1]]
                    });
                }
            } else { chartRef.current == ChartTypes.overview && [setChartFilters(null), getUsageListData(0)] }
        } else {
            isMountedGeneralView.current = true;
        }
    }, [activeOverviewIndex]);

    useEffect(() => {
        if (isMountedRequests.current) {
            if (activeRequestsIndex != null) {
                chartRef.current = ChartTypes.requests;
                setActiveUsageIndex(null);
                setActiveTeamUsageIndex(null);
                setUsageListPageNumber(0);
                setChartFilters({
                    chartType: ChartTypes.requests,
                    value: formatDateByMonth(type === DashboardTypes.general ? requests?.requestMetrics[activeRequestsIndex]?.dateString : requests?.requestTeamMetrics[activeRequestsIndex]?.dateString) ?? null,
                });
            } else { chartRef.current == ChartTypes.requests && [setChartFilters(null), getUsageListData(0)] }
        } else {
            isMountedRequests.current = true;
        }
    }, [activeRequestsIndex]);

    useEffect(() => {
        if (isMountedRequestsStatus.current) {
            if (activeRequestsStatusIndex != null && (activeRequestsStatusIndex[0] != null && activeRequestsStatusIndex[1] != null)) {
                chartRef.current = ChartTypes.requestsStatus;
                setActiveUsageIndex(null);
                setActiveTeamUsageIndex(null);
                setUsageListPageNumber(0);
                setChartFilters({
                    chartType: ChartTypes.requestsStatus,
                    value: formatDateByMonth(type === DashboardTypes.general ? requests?.requestsStatusMetrics[activeRequestsStatusIndex[0]]?.dateString : requests?.requestsTeamStatusMetrics[activeRequestsStatusIndex[0]]?.dateString) ?? null,
                    additional: type === DashboardTypes.general ? requests?.requestsStatusMetrics[activeRequestsStatusIndex[0]].metrics[activeRequestsStatusIndex[1]]?.value : requests?.requestsTeamStatusMetrics[activeRequestsStatusIndex[0]].metrics[activeRequestsStatusIndex[1]]?.value ?? null,
                    index: [activeRequestsStatusIndex[0], activeRequestsStatusIndex[1]]
                });
            } else { chartRef.current == ChartTypes.requestsStatus && [setChartFilters(null), getUsageListData(0)] }
        } else {
            isMountedRequestsStatus.current = true;
        }
    }, [activeRequestsStatusIndex]);

    useEffect(() => {
        if (isMountedUsage.current) {
            if (activeUsageIndex != null) {
                chartRef.current = ChartTypes.usage;
                setActiveRequestsIndex(null);
                setActiveTeamUsageIndex(null);
                setActiveRequestsStatusIndex(null);
                setUsageListPageNumber(0);
                setChartFilters({
                    chartType: ChartTypes.usage,
                    value: usage?.usageMetrics[activeUsageIndex]?.label ?? null,
                });
            } else { chartRef.current == ChartTypes.usage && [setChartFilters(null), getUsageListData(0)] }
        } else {
            isMountedUsage.current = true;
        }
    }, [activeUsageIndex]);

    useEffect(() => {
        if (isMountedTeamUsage.current) {
            if (activeTeamUsageIndex != null && (activeTeamUsageIndex[0] != null && activeTeamUsageIndex[1] != null)) {
                chartRef.current = ChartTypes.teamUsage;
                setActiveRequestsIndex(null);
                setActiveUsageIndex(null);
                setActiveRequestsStatusIndex(null);
                setUsageListPageNumber(0);
                setChartFilters({
                    chartType: ChartTypes.teamUsage,
                    value: usage?.usageConsolidatedMetrics[activeTeamUsageIndex[0]]?.team ?? null,
                    additional: usage?.usageConsolidatedMetrics[activeTeamUsageIndex[0]]?.metrics[activeTeamUsageIndex[1]]?.label ?? null,
                    index: [activeTeamUsageIndex[0], activeTeamUsageIndex[1]]
                });
            } else { chartRef.current == ChartTypes.teamUsage && [setChartFilters(null), getUsageListData(0)] }
        } else {
            isMountedTeamUsage.current = true;
        }
    }, [activeTeamUsageIndex]);

    useEffect(() => {
        if (isMountedRealizedRequests.current) {
            if (activeRealizedRequestsIndex != null) {
                chartRef.current = ChartTypes.realizedRequests;
                setActiveUsageIndex(null);
                setActiveTeamUsageIndex(null);
                setRealizedUsageListPageNumber(0);
                setChartFilters({
                    chartType: ChartTypes.realizedRequests,
                    value: formatDateByMonth(type === DashboardTypes.general ? requests?.requestRealizedMetrics[activeRealizedRequestsIndex]?.dateString : requests?.requestRealizedTeamMetrics[activeRealizedRequestsIndex]?.dateString) ?? null,
                });
            } else { chartRef.current == ChartTypes.realizedRequests && [setChartFilters(null), getRealizedUsageListData(0)] }
        } else {
            isMountedRealizedRequests.current = true;
        }
    }, [activeRealizedRequestsIndex]);

    useEffect(() => {
        if (isMountedRealizedUsage.current) {
            if (activeUsageIndex != null) {
                chartRef.current = ChartTypes.usage;
                setActiveRealizedRequestsIndex(null);
                setActiveTeamUsageIndex(null);
                setRealizedUsageListPageNumber(0);
                setChartFilters({
                    chartType: ChartTypes.usage,
                    value: usage?.usageMetrics[activeUsageIndex]?.label ?? null,
                });
            } else { chartRef.current == ChartTypes.usage && [setChartFilters(null), getRealizedUsageListData(0)] }
        } else {
            isMountedRealizedUsage.current = true;
        }
    }, [activeUsageIndex]);

    useEffect(() => {
        if (isMountedTeamRealizedUsage.current) {
            if (activeTeamUsageIndex != null && (activeTeamUsageIndex[0] != null && activeTeamUsageIndex[1] != null)) {
                chartRef.current = ChartTypes.teamUsage;
                setActiveRealizedRequestsIndex(null);
                setActiveUsageIndex(null);
                setRealizedUsageListPageNumber(0);
                setChartFilters({
                    chartType: ChartTypes.teamUsage,
                    value: usage?.usageConsolidatedMetrics[activeTeamUsageIndex[0]]?.team ?? null,
                    additional: usage?.usageConsolidatedMetrics[activeTeamUsageIndex[0]]?.metrics[activeTeamUsageIndex[1]]?.label ?? null,
                    index: [activeTeamUsageIndex[0], activeTeamUsageIndex[1]]
                });
            } else { chartRef.current == ChartTypes.teamUsage && [setChartFilters(null), getRealizedUsageListData(0)] }
        } else {
            isMountedTeamRealizedUsage.current = true;
        }
    }, [activeTeamUsageIndex]);

    useEffect(() => {
        if (isMountedRating.current) {
            if (activeRatingIndex != null) {
                chartRef.current = ChartTypes.rating;
                setActiveTeamRatingIndex(null);
                setRatingListPageNumber(0);
                setChartFilters({
                    chartType: ChartTypes.rating,
                    value: (type == DashboardTypes.general ? rating?.ratingMetrics[activeRatingIndex]?.value : rating?.ratingTeamMetrics[activeRatingIndex]?.value) ?? null,
                });
            } else { chartRef.current == ChartTypes.rating && [setChartFilters(null), getRatingListData(0)] }
        } else {
            isMountedRating.current = true;
        }
    }, [activeRatingIndex]);

    useEffect(() => {
        if (isMountedTeamRating.current) {
            if (activeTeamRatingIndex != null && (activeTeamRatingIndex[0] != null && activeTeamRatingIndex[1] != null)) {
                chartRef.current = ChartTypes.teamRating;
                setActiveRatingIndex(null);
                setRatingListPageNumber(0);
                setChartFilters({
                    chartType: ChartTypes.teamRating,
                    value: rating?.ratingUsageConsolidatedMetrics[activeTeamRatingIndex[0]]?.team ?? null,
                    additional: rating?.ratingUsageConsolidatedMetrics[activeTeamRatingIndex[0]]?.metrics[activeTeamRatingIndex[1]]?.label ?? null,
                    index: [activeTeamRatingIndex[0], activeTeamRatingIndex[1]]
                });
            } else { chartRef.current == ChartTypes.teamRating && [setChartFilters(null), getRatingListData(0)] }
        } else {
            isMountedTeamRating.current = true;
        }
    }, [activeTeamRatingIndex]);

    useEffect(() => {
        if (chartFilters && menuIndex === 0) {
            getUsageListData(0, chartFilters);
        }

        if (chartFilters && menuIndex === 1) {
            getUsageListData(0, chartFilters);
        }

        if (chartFilters && menuIndex === 2) {
            getRealizedUsageListData(0, chartFilters);
        }

        if (chartFilters && menuIndex === 3) {
            if (!unitFilter) {
                getFilteredRequestsData(chartFilters.value);
                setUnitFilter(chartFilters.value);
            } else if (unitFilter && unitFilter !== chartFilters.value) {
                getFilteredRequestsData(chartFilters.value);
                setUnitFilter(chartFilters.value);
            } else {
                getRequestsData();
                setUnitFilter(undefined);
                setChartFilters(null);
            }

        }

        if (chartFilters && menuIndex === 4) {
            getRatingListData(0, chartFilters);
        }
    }, [chartFilters]);

    const init = async () => {
        if (type === DashboardTypes.general) {
            await Promise.all([
                getRequestsData(true),
                getUsageListData(0)
            ]).then(() => {
                setLoading(false);
            });
        }

        if (type === DashboardTypes.team) {
            await Promise.all([
                getRequestsData(true),
                getUsageListData(0)
            ]).then(() => {
                setLoading(false);
            });
        }
    }

    const sessionChange = async (currentTarget, props) => {
        setActiveRequestsIndex(null);
        setActiveUsageIndex(null);
        setActiveTeamUsageIndex(null);
        setActiveRequestsStatusIndex(null);
        setActiveRatingIndex(null);
        setActiveTeamRatingIndex(null);
        setChartFilters(null);
        setUnitFilter(undefined);

        await Promise.all([setLoadingCharts(true), setMenuIndex(props.index)])
            .then(async () => {
                if (props.index === 0) {
                    await Promise.all([getRequestsData(true), getUsageListData(0)]).then(() => {
                        setLoadingCharts(false);
                        setUsageListPageNumber(0);
                    });
                }

                if (props.index === 1) {
                    if (type === DashboardTypes.general) {
                        await Promise.all([getRequestsData()]).then(() => {
                            setLoadingCharts(false);
                            setUsageListPageNumber(0);
                        });
                    } else {
                        await Promise.all([getRequestsData(), getUsageData(), getUsageListData(0)]).then(() => {
                            setLoadingCharts(false);
                            setUsageListPageNumber(0);
                        });
                    }
                }

                if (props.index === 2) {
                    if (type === DashboardTypes.general) {
                        await Promise.all([getRequestsData(true), getRealizedUsageListData(0)]).then(() => {
                            setLoadingCharts(false);
                            setRealizedUsageListPageNumber(0);
                        });
                    } else {
                        await Promise.all([getRequestsData(true), getUsageData(true), getRealizedUsageListData(0)]).then(() => {
                            setLoadingCharts(false);
                            setRealizedUsageListPageNumber(0);
                        });
                    }
                }

                if (props.index === 3) {
                    if (type === DashboardTypes.general) {
                        await Promise.all([getRequestsData(true), getMeetingDataListData()]).then(() => {
                            setLoadingCharts(false);
                        });
                    } else {
                        //await Promise.all([getRequestsData(true), getUsageData(true), getRealizedUsageListData(0)]).then(() => {
                        //    setLoadingCharts(false);
                        //    setRealizedUsageListPageNumber(0);
                        //});
                    }
                }

                if (props.index === 4) {
                    await Promise.all([getRatingData(), getRatingListData(0)]).then(() => {
                        setLoadingCharts(false);
                        setRatingListPageNumber(0);
                    });
                }
            });
    }

    const getRequestsData = async (overview?: boolean) => {
        const response = await getRequests(type, overview);

        if (response.status === 200 && response.data) {
            setRequests(response.data);
            overview && setRealizedRequests(response.data);
        }
    }

    const getFilteredRequestsData = async (filter?: string) => {
        const response = await getRequests(type, false, filter);

        if (response.status === 200 && response.data) {
            setRequests(response.data);
        }
    }

    const getUsageData = async (realized?: boolean) => {
        const response = await getUsage(realized);

        if (response.status === 200 && response.data)
            setUsage(response.data);
    }

    const getUsageListData = async (offset: number, filters?: any) => {
        setUsageList(null);

        const response = await getUsageList(type, (offset ?? 0), 10, (filters ?? null));

        if (response.status === 200 && response.data) {
            setUsageList(response.data.meetingQueueData?.map((r, i) => {
                return {
                    key: i,
                    items: [listProtocolCell(r), formatDate(r.created), (r.assunto ?? ""), listUserCell(r), (r.atendenteName ?? ""), listRatingCell(r)],
                    onClick: () => { }
                }
            }));
            setUsageListCount(response.data.totalCount);
        }
    }

    const getRealizedUsageListData = async (offset: number, filters?: any) => {
        setRealizedUsageList(null);

        const response = await getRealizedUsageList(type, (offset ?? 0), 10, (filters ?? null));

        if (response.status === 200 && response.data) {
            setRealizedUsageList(response.data.meetingQueueData?.map((r, i) => {
                return {
                    key: i,
                    items: [listProtocolCell(r), formatDate(r.created), (r.assunto ?? ""), listUserCell(r), (r.atendenteName ?? ""), listRatingCell(r)],
                    onClick: () => { }
                }
            }));
            setRealizedUsageListCount(response.data.totalCount);
        }
    }

    const getRatingData = async () => {
        const response = await getRating(type);

        if (response.status === 200 && response.data)
            setRating(response.data)
    }

    const getRatingListData = async (offset: number, filters?: any) => {
        setRatingList(null);

        const response = await getRatingList(type, (offset ?? 0), 10, (filters ?? null));

        if (response.status === 200 && response.data) {
            setRatingList(response.data.meetingQueueData?.map((r, i) => {
                return {
                    key: i,
                    items: [listProtocolCell(r), formatDate(r.created), (r.assunto ?? ""), listUserCell(r), r.atendenteName, listRatingCell(r), (r.Rating.Comments ?? "")],
                }
            }));
            setRatingListCount(response.data.totalCount);
        }
    }

    const getMeetingDataListData = async () => {
        const response = await getMeetingDataList();

        if (response.status === 200 && response.data) {
            console.log(response.data)
            const meetingDataResponse = response.data.meetingDataMetrics.map(m => ({
                label: m.label,
                sum: m.sum,
                realizedSum: m.totalAtendimentos,
                name: m.name,
                metadata: m.metadata,
                categoria: m.categoria,
                totalAtendentes: m.totalAtendentes,
                tempoMedioEsperaMin: m.tempoMedioEsperaMin,
                tempoMedioAtendimentoMin: m.tempoMedioAtendimentoMin,
                percent: m.sum > 0 ? Math.round((m.totalAtendimentos / m.sum) * 100) : 0
            }));

            setMeetingDataList(meetingDataResponse);
            setOriginalMeetingDataList(meetingDataResponse);
            setUnitCategories(response.data.meetingDataCategories);
        }
    }

    const transformStatusRequestsConsolidatedMetrics = (inputData: any) => {
        const labels = [
            { original: "Na fila", newLabel: "Na fila" },
            { original: "Em atendimento", newLabel: "Em atendimento" },
            { original: "Finalizado", newLabel: "Finalizado" },
            { original: "Expirado", newLabel: "Expirado" },
        ];

        let datasets = labels.map((label) => {
            const dataForLabel = inputData.map((monthData) => {
                const foundMetric = monthData.metrics.find((metric) =>
                    metric.value.trim().toLowerCase() === label.original.trim().toLowerCase()
                );

                const sum = foundMetric ? foundMetric.count : 0;

                return sum;
            });

            return {
                label: label.newLabel,
                data: dataForLabel,
            };
        });

        inputData.forEach((monthData) => {
            const existingStatuses = monthData.metrics.map((metric) => metric.value);

            labels.forEach((label) => {
                if (!existingStatuses.includes(label.original)) {
                    monthData.metrics.push({
                        value: label.original,
                        count: 0,
                        month: monthData.month,
                    });
                }
            });

            monthData.metrics.sort((a, b) => {
                const order = ["Expirado", "Finalizado", "Em atendimento", "Na fila"];
                return order.indexOf(a.value) - order.indexOf(b.value);
            });
        });

        datasets = datasets.reverse();

        return { datasets };
    };

    const transformUsageConsolidatedMetrics = (inputData: any) => {
        const labels = inputData.map(team => team.teamName);

        const metricsMap: { [key: string]: boolean } = {};

        inputData.forEach(team => {
            team.metrics.forEach(metric => { metricsMap[metric.label] = true });
        });

        const metrics: string[] = Object.keys(metricsMap);

        const datasets = metrics.map(label => {
            const data = inputData.map(team => {
                const metric = team.metrics.find(metric => metric.label === label);
                return metric ? metric.value : 0;
            });
            return { label, data };
        });

        const height = datasets[0].data.length * 55 + "px";

        return { labels, datasets, height };
    }

    const transformRatingConsolidatedMetrics = (inputData: any) => {
        const labels = inputData.map(team => team.teamName);

        const metricsMap: { [key: number]: boolean } = {};

        inputData.forEach(team => {
            team.metrics.forEach(metric => { metricsMap[metric.label] = true });
        });

        const metrics: number[] = Object.keys(metricsMap).map(Number);

        const datasets = metrics.map(label => {
            const data = inputData.map(team => {
                const metric = team.metrics.find(metric => metric.label === label);
                return metric ? metric.value : 0;
            });

            let labelSuffix = formatRatingLabel(label);

            return { label: labelSuffix, data };
        });

        const height = datasets[0].data.length * 75 + "px";

        return { labels, datasets, height };
    }

    const listProtocolCell = (result: any) => {
        let statusBgColor = "#feebc0";
        let statusColor = "#a07943";

        switch (result.status) {
            case statusQueueConstants.naFila:
                statusBgColor = '#feebc0';
                statusColor = '#a07943';
                break;
            case statusQueueConstants.atendimentoIniciado:
                statusBgColor = '#d4e7f6';
                statusColor = '#2e73b3';
                break;
            case statusQueueConstants.emAtendimento:
                statusBgColor = '#d4e7f6';
                statusColor = '#2e73b3';
                break;
            case statusQueueConstants.atendimentoEncerrado:
                statusBgColor = '#cbf1cc';
                statusColor = '#44772f';
                break;
            case statusQueueConstants.atendimentoCancelado:
                statusBgColor = '#fabbc3';
                statusColor = '#a72e35';
                break;
            case statusQueueConstants.atendimentoInativo:
                statusBgColor = '#fabbc3';
                statusColor = '#a72e35';
                break;
        }

        return {
            content: (
                <Flex gap="gap.smaller" vAlign="center" column>
                    <Flex gap="gap.smaller" vAlign="center">
                        <Text className="protocol" content={result.AdditionalData?.prefix ? result.AdditionalData?.prefix + result.id.slice(-6) : "#" + result.id.slice(-6)} weight="semibold" />
                        <Text className="tag" style={{ color: statusColor, backgroundColor: statusBgColor }} content={result.status} />
                    </Flex>

                    <Text className="tag" content={result.nome} />
                </Flex>
            ),
            onClick: () => { onViewHistory(result.id, result.Ancora) }
        }
    }

    const listUserCell = (result: any) => {
        return {
            content: (
                <Flex gap="gap.smaller" vAlign="center" column>
                    <Text content={result.UserProfile?.userName ?? ""} />
                    {result.AdditionalData?.profileType && <Text className="tag" content={result.AdditionalData?.profileType ?? ""} />}
                </Flex>
            )
        }
    }

    const listRatingCell = (result: any) => {
        return {
            content: (
                <Flex gap="gap.small" vAlign="center">
                    <div
                        className="rating"
                        style={{
                            backgroundColor: (() => {
                                switch (result.Rating?.Rate) {
                                    case 5: return '#a0c15a';
                                    case 4: return '#add633';
                                    case 3: return '#ffd934';
                                    case 2: return '#ffb234';
                                    case 1: return '#ff8c5a';
                                }
                            })(),
                        }}>
                    </div>
                    <Text content={result.Rating?.Rate ? formatRatingLabel(result.Rating.Rate) : ""} />
                </Flex>
            ),
        }
    }

    const formatRatingLabel = (label: number) => {
        let labelSuffix = "";

        switch (label) {
            case 5:
                labelSuffix = label.toString() + " - Ótimo";
                break;
            case 4:
                labelSuffix = label.toString() + " - Bom";
                break;
            case 3:
                labelSuffix = label.toString() + " - Regular";
                break;
            case 2:
                labelSuffix = label.toString() + " - Insuficiente";
                break;
            case 1:
                labelSuffix = label.toString() + " - Fraco";
                break;
        }

        return labelSuffix
    }

    const formatDate = (dateString: string) => {
        const date = new Date(dateString);

        const formattedDate = date.toLocaleString('pt-BR', {
            hour12: false,
            day: '2-digit',
            month: '2-digit',
            year: '2-digit',
        }).replace(',', '').replace(/^(\d{2})\/(\d{2})\/(\d{4})$/, '$1/$2/$3');

        const formattedTime = date.toLocaleString('pt-BR', {
            hour12: false,
            hour: '2-digit',
            minute: '2-digit',
        }).replace(',', '');

        return (
            <>
                <div>{formattedDate}</div>
                <div>{formattedTime}</div>
            </>
        );
    }

    const formatDateString = (dateString: string) => {
        ;
        const monthName = Data.monthFullNames[new Date(dateString).getUTCMonth()];
        const shortYear = new Date(dateString).getUTCFullYear().toString().slice(-2);

        return `${monthName.charAt(0).toUpperCase() + monthName.slice(1)}/${shortYear}`;
    }

    const formatDateByMonth = (dateString: number) => {
        return dateString.toString();
    }

    const formatTime = (time: number) => {
        const totalSeconds = Math.round(time * 60);
        const h = Math.floor(totalSeconds / 3600);
        const m = Math.floor((totalSeconds % 3600) / 60);
        const s = totalSeconds % 60;

        const pad = (n) => String(n).padStart(2, '0');
        return `${pad(h)}:${pad(m)}:${pad(s)}`;
    }

    const getMonthName = (date: number) => {
        const parsedDate = new Date(date);
        const month = parsedDate.getUTCMonth() + 1;

        return Data.monthFullNames[month - 1];
    }

    const onViewHistory = (queueId: string, ancora: string) => {
        let taskInfo: UrlDialogInfo = {
            url: window.location.origin + `/post-meeting-panel/${queueId}/${ancora}`,
            title: "Detalhes",
            size: {
                height: 650,
                width: 930,
            },
        }

        dialog.url.open(taskInfo);
    }

    const renderVisaoGeral = () => {
        return (
            loadingCharts ?
                <Loader />
                :
                <Flex gap="gap.medium" column>
                    <Segment className="container">
                        <Flex className="leftPanel" gap="gap.large" padding="padding.medium" column>
                            {/*<Text className="title" content="Visão geral" size="medium" weight="semibold" />*/}

                            <Flex gap="gap.medium" column>
                                <Flex className="containerBorder requests" gap="gap.smaller" column>
                                    <Text
                                        className="textHeight"
                                        content={new Intl.NumberFormat('pt-BR').format(requests?.totalCount)}
                                        size="larger"
                                        weight="bold"
                                    />
                                    <Text className="description" content="Total de novas solicitações" size="small" />
                                </Flex>

                                <Flex className="containerBorder realized" gap="gap.smaller" column>
                                    <Text
                                        className="textHeight"
                                        content={new Intl.NumberFormat('pt-BR').format(realizedRequests.realizedTotalCount)}
                                        size="larger"
                                        weight="bold"
                                    />
                                    <Text className="description" content="Total de solicitações atendidas" size="small" />
                                </Flex>

                                <Text className="descriptionHighlighted" content="Indicador de solicitações atendidas considera os atendimentos finalizados, com status encerrado ou cancelado. " size="small" />
                            </Flex>
                        </Flex>
                        <Flex className="rightPanel" gap="gap.medium" padding="padding.medium" column>
                            <Flex gap="gap.smaller" column>
                                {/*<Text content="Visão geral por mês" size="medium" weight="semibold" />*/}
                                <Text className="description" content="Resumo mensal de novas solicitações recebidas e de atendimentos finalizados. O indicador de atendimentos finalizados não considera a data de abertura da solicitação." size="small" />
                            </Flex>

                            <Flex className="chartContainer" gap="gap.smaller" column>
                                <Flex className="chartSolicitacoes" column>
                                    <FluentUIThemeConsumer
                                        render={(globalTheme) => {
                                            return (
                                                <MemBarChart
                                                    {...{
                                                        title: "Solicitações",
                                                        data: {
                                                            labels: type === DashboardTypes.general ?
                                                                requests?.requestMetrics?.map(item => getMonthName(item.dateString)) :
                                                                requests?.requestTeamMetrics?.map(item => getMonthName(item.dateString)),
                                                            datasets: [
                                                                {
                                                                    label: "Novas solicitações",
                                                                    data: type === DashboardTypes.general ?
                                                                        requests?.requestMetrics?.map(item => item.count) :
                                                                        requests?.requestTeamMetrics?.map(item => item.count),
                                                                },
                                                                {
                                                                    label: "Solicitações atendidas",
                                                                    data: type === DashboardTypes.general ?
                                                                        realizedRequests?.requestRealizedMetrics?.map(item => item.count) :
                                                                        realizedRequests?.requestRealizedTeamMetrics?.map(item => item.count),
                                                                }
                                                            ],
                                                        },
                                                        siteVariables: globalTheme.siteVariables,
                                                        theme: theme,
                                                        stacked: false,
                                                        customColors: requestsColors,
                                                        activeSegmentIndex: activeOverviewIndex,
                                                        onChangeSegmentIndex: (index, datasetIndex) => {
                                                            setOverviewIndex([index, datasetIndex]);
                                                        }
                                                    }}
                                                />
                                            )
                                        }}
                                    />
                                </Flex>
                            </Flex>
                        </Flex>
                    </Segment>

                    <Segment className="container">
                        <Flex className="rightPanel full" gap="gap.medium" padding="padding.medium" column>
                            <Flex gap="gap.medium" column>
                                <Table
                                    className="chartTable chartTableUsage"
                                    header={{
                                        items: ['Protocolo', 'Data', 'Assunto', 'Solicitante', 'Atendente', 'Avaliação'],
                                    }}
                                    rows={usageList}
                                />

                                <div className="pagination">
                                    <Pagination
                                        callbackFromParent={async (pageNumber: number) => {
                                            setUsageListPageNumber(pageNumber);

                                            await getUsageListData(pageNumber * 10, chartFilters);
                                        }}
                                        entitiesLength={usageListCount}
                                        activePage={usageListPageNumber}
                                        numberOfContents={10}
                                    />
                                </div>
                            </Flex>
                        </Flex>
                    </Segment>
                </Flex>
        );
    }

    const renderSolicitacoes = () => {
        return (
            loadingCharts ?
                <Loader />
                :
                <Flex gap="gap.medium" column>
                    <Segment className="container">
                        <Flex className="leftPanel" gap="gap.medium" padding="padding.medium" column>
                            {/*<Text className="title" content="Total de novas solicitações" size="medium" weight="semibold" />*/}

                            <Flex className="containerBorder" gap="gap.smaller" column>
                                <Text
                                    className="textHeight"
                                    content={new Intl.NumberFormat('pt-BR').format(requests?.totalCount)}
                                    size="larger"
                                    weight="bold"
                                />
                                <Text className="description" content="Total de novas solicitações" size="small" />
                            </Flex>

                            <Flex gap="gap.smaller" column>
                                <Flex className="containerBorder" style={{ borderColor: requestsStatusColors[0] }} gap="gap.smaller">
                                    {type === DashboardTypes.general ?
                                        <Text
                                            className="textHeight"
                                            content={new Intl.NumberFormat('pt-BR').format((requests?.requestsStatusTotalsMetrics?.find(item => item.value === statusQueueConstants.naFila)?.count ?? 0))}
                                            size="large"
                                            weight="bold"
                                        />
                                        :
                                        <Text
                                            className="textHeight"
                                            content={new Intl.NumberFormat('pt-BR').format((requests?.requestsTeamStatusTotalsMetrics?.find(item => item.value === statusQueueConstants.naFila)?.count ?? 0))}
                                            size="large"
                                            weight="bold"
                                        />
                                    }
                                    <Text className="description" content="Na fila" size="small" />
                                </Flex>

                                <Flex className="containerBorder" style={{ borderColor: requestsStatusColors[1] }} gap="gap.smaller">
                                    {type === DashboardTypes.general ?
                                        <Text
                                            className="textHeight"
                                            content={
                                                new Intl.NumberFormat('pt-BR').format(
                                                    ((requests?.requestsStatusTotalsMetrics?.find(item => item.value === statusQueueConstants.atendimentoIniciado)?.count ?? 0) +
                                                        (requests?.requestsStatusTotalsMetrics?.find(item => item.value === statusQueueConstants.emAtendimento)?.count ?? 0))
                                                )
                                            }
                                            size="large"
                                            weight="bold"
                                        />
                                        :
                                        <Text
                                            className="textHeight"
                                            content={
                                                new Intl.NumberFormat('pt-BR').format(
                                                    ((requests?.requestsTeamStatusTotalsMetrics?.find(item => item.value === statusQueueConstants.atendimentoIniciado)?.count ?? 0) +
                                                        (requests?.requestsTeamStatusTotalsMetrics?.find(item => item.value === statusQueueConstants.emAtendimento)?.count ?? 0))
                                                )
                                            }
                                            size="large"
                                            weight="bold"
                                        />
                                    }

                                    <Text className="description" content="Em atendimento" size="small" />
                                </Flex>

                                <Flex className="containerBorder" style={{ borderColor: requestsStatusColors[2] }} gap="gap.smaller">
                                    {type === DashboardTypes.general ?
                                        <Text
                                            className="textHeight"
                                            content={
                                                new Intl.NumberFormat('pt-BR').format(
                                                    ((requests?.requestsStatusTotalsMetrics?.find(item => item.value === statusQueueConstants.atendimentoEncerrado)?.count ?? 0) +
                                                        (requests?.requestsStatusTotalsMetrics?.find(item => item.value === statusQueueConstants.atendimentoCancelado)?.count ?? 0))
                                                )
                                            }
                                            size="large"
                                            weight="bold"
                                        />
                                        :
                                        <Text
                                            className="textHeight"
                                            content={
                                                new Intl.NumberFormat('pt-BR').format(
                                                    ((requests?.requestsTeamStatusTotalsMetrics?.find(item => item.value === statusQueueConstants.atendimentoEncerrado)?.count ?? 0) +
                                                        (requests?.requestsTeamStatusTotalsMetrics?.find(item => item.value === statusQueueConstants.atendimentoCancelado)?.count ?? 0))
                                                )
                                            }
                                            size="large"
                                            weight="bold"
                                        />
                                    }

                                    <Text className="description" content="Finalizado" size="small" />
                                </Flex>

                                {type === DashboardTypes.general &&
                                    <Flex className="containerBorder" style={{ borderColor: requestsStatusColors[3] }} gap="gap.smaller">
                                        <Text
                                            className="textHeight"
                                            content={
                                                new Intl.NumberFormat('pt-BR').format((requests?.requestsStatusTotalsMetrics?.find(item => item.value === statusQueueConstants.atendimentoInativo)?.count ?? 0))
                                            }
                                            size="large"
                                            weight="bold"
                                        />

                                        <Text className="description" content="Expirado" size="small" />
                                    </Flex>
                                }
                            </Flex>

                        </Flex>
                        <Flex className="rightPanel" gap="gap.medium" padding="padding.medium" column>
                            <Flex gap="gap.smaller" column>
                                {/*<Text content="Novas solicitações por mês" size="medium" weight="semibold" />*/}
                                <Text className="description" content="Resumo mensal de novas solicitações recebidas, agrupadas por status de atendimento." size="small" />
                            </Flex>

                            <Flex className="chartContainer" gap="gap.smaller" column>
                                <Flex className="chartSolicitacoes" column>
                                    <FluentUIThemeConsumer
                                        render={(globalTheme) => {
                                            return (
                                                <MemBarChart
                                                    {...{
                                                        title: "Solicitações",
                                                        data: {
                                                            labels: type === DashboardTypes.general ?
                                                                requests?.requestMetrics?.map(item => getMonthName(item.dateString)) :
                                                                requests?.requestTeamMetrics?.map(item => getMonthName(item.dateString)),
                                                            datasets: type === DashboardTypes.general ? transformStatusRequestsConsolidatedMetrics(requests?.requestsStatusMetrics).datasets :
                                                                transformStatusRequestsConsolidatedMetrics(requests?.requestsTeamStatusMetrics).datasets,
                                                        },
                                                        siteVariables: globalTheme.siteVariables,
                                                        theme: theme,
                                                        customColors: requestsStatusColors.reverse(),
                                                        stacked: true,
                                                        customDataLabel: true,
                                                        activeSegmentIndex: activeRequestsStatusIndex,
                                                        onChangeSegmentIndex: (index, datasetIndex) => {
                                                            setActiveRequestsStatusIndex([index, datasetIndex]);
                                                        }
                                                    }}
                                                />
                                            )
                                        }}
                                    />
                                </Flex>
                            </Flex>
                        </Flex>
                    </Segment>

                    {type === DashboardTypes.team &&
                        <Segment className="container">
                            <Flex className="leftPanel semilarge" gap="gap.medium" padding="padding.medium" column>
                                <Text content="Perfis de uso" size="medium" weight="semibold" />

                                <Flex className="chartContainer" gap="gap.smaller" column>
                                    <Flex className="chartUso" column>
                                        <FluentUIThemeConsumer
                                            render={(globalTheme) => {
                                                return (
                                                    <MemDoughnutChart
                                                        {...{
                                                            title: "Perfil de uso",
                                                            data: {
                                                                labels: usage?.usageMetrics?.map(item => item.label),
                                                                datasets: [
                                                                    {
                                                                        label: "",
                                                                        data: usage?.usageMetrics?.map(item => item.sum),
                                                                    }
                                                                ],
                                                            },
                                                            siteVariables: globalTheme.siteVariables,
                                                            theme: theme,
                                                            cutoutPercentage: 70,
                                                            borderWidth: 2,
                                                            customColors: usageColors,
                                                            collapsed: true,
                                                            custom: true,
                                                            activeSegmentIndex: activeUsageIndex,
                                                            onChangeSegmentIndex: (index) => {
                                                                setActiveUsageIndex(index);
                                                            }
                                                        }}
                                                    />
                                                )
                                            }}
                                        />
                                    </Flex>

                                    <Text className="descriptionHighlighted" content="Visão de novas solicitações agrupadas por perfil de uso (Geral, Magistrados e Plantão). Selecione um dos perfis para refletir na listagem abaixo." size="small" />
                                </Flex>
                            </Flex>
                            <Flex className="rightPanel" gap="gap.medium" padding="padding.medium" column>
                                <Flex gap="gap.smaller" column>
                                    <Text content="Uso por equipe de atendimento" size="medium" weight="semibold" />
                                    <Text className="description" content="Visão de novas solicitações agrupadas por perfil de uso e equipe de atendimento." size="small" />
                                </Flex>

                                <Flex id="chartContainerScrollable" className="chartContainerScrollable" gap="gap.smaller" column>
                                    <Flex id="chartEquipe" className="chartEquipe" gap="gap.small" column>

                                        <div style={{ height: transformUsageConsolidatedMetrics(usage?.usageConsolidatedMetrics).height }}>
                                            <FluentUIThemeConsumer
                                                render={(globalTheme) => {
                                                    return (
                                                        <MemBarHorizontalChart
                                                            {...{
                                                                title: "Uso por equipe de atendimento",
                                                                data: transformUsageConsolidatedMetrics(usage?.usageConsolidatedMetrics),
                                                                siteVariables: globalTheme.siteVariables,
                                                                theme: theme,
                                                                customColors: teamUsageColors,
                                                                customHeight: transformUsageConsolidatedMetrics(usage?.usageConsolidatedMetrics).height,
                                                                stacked: true,
                                                                layout: {
                                                                    top: 0,
                                                                    right: 10,
                                                                    left: -45,
                                                                },
                                                                mirror: true,
                                                                yLabelOffset: -20,
                                                                barPercentage: 0.6,
                                                                categoryPercentage: 0.5,
                                                                hideLegend: true,
                                                                activeSegmentIndex: activeTeamUsageIndex,
                                                                onChangeSegmentIndex: (index, datasetIndex) => {
                                                                    setActiveTeamUsageIndex([index, datasetIndex]);
                                                                }
                                                            }}
                                                        />
                                                    )
                                                }}
                                            />
                                        </div>
                                    </Flex>
                                </Flex>
                            </Flex>
                        </Segment>
                    }

                    <Segment className="container">
                        <Flex className="rightPanel full" gap="gap.medium" padding="padding.medium" column>
                            <Flex gap="gap.medium" column>
                                <Table
                                    className="chartTable chartTableUsage"
                                    header={{
                                        items: ['Protocolo', 'Data', 'Assunto', 'Solicitante', 'Atendente', 'Avaliação'],
                                    }}
                                    rows={usageList}
                                />

                                <div className="pagination">
                                    <Pagination
                                        callbackFromParent={async (pageNumber: number) => {
                                            setUsageListPageNumber(pageNumber);

                                            await getUsageListData(pageNumber * 10, chartFilters);
                                        }}
                                        entitiesLength={usageListCount}
                                        activePage={usageListPageNumber}
                                        numberOfContents={10}
                                    />
                                </div>
                            </Flex>
                        </Flex>
                    </Segment>
                </Flex>
        );
    }

    const renderAtendimentos = () => {
        return (
            loadingCharts ?
                <Loader />
                :
                <Flex gap="gap.medium" column>
                    <Segment className="container">
                        <Flex className="leftPanel" gap="gap.large" padding="padding.medium" column>
                            {/*<Text className="title" content="Total de novas solicitações" size="medium" weight="semibold" />*/}

                            <Flex className="containerBorder realized" gap="gap.smaller" column>
                                <Text
                                    className="textHeight"
                                    content={new Intl.NumberFormat('pt-BR').format(requests?.realizedTotalCount)}
                                    size="larger"
                                    weight="bold"
                                />
                                <Text className="description" content="Total de solicitações atendidas" size="small" />
                            </Flex>

                            <Text className="descriptionHighlighted" content="Indicador de solicitações atendidas considera os atendimentos finalizados, com status encerrado ou cancelado. " size="small" />
                        </Flex>
                        <Flex className="rightPanel" gap="gap.medium" padding="padding.medium" column>
                            <Flex gap="gap.smaller" column>
                                {/*<Text content="Novas solicitações por mês" size="medium" weight="semibold" />*/}
                                <Text className="description" content="Resumo mensal de atendimentos finalizados. O indicador de atendimentos finalizados não considera a data de abertura da solicitação." size="small" />
                            </Flex>

                            <Flex className="chartContainer" gap="gap.smaller" column>
                                <Flex className="chartSolicitacoes" column>
                                    <FluentUIThemeConsumer
                                        render={(globalTheme) => {
                                            return (
                                                <MemBarChart
                                                    {...{
                                                        title: "Solicitações",
                                                        data: {
                                                            labels: type === DashboardTypes.general ?
                                                                requests?.requestRealizedMetrics?.map(item => getMonthName(item.month)) :
                                                                requests?.requestRealizedTeamMetrics?.map(item => getMonthName(item.month)),
                                                            datasets: [
                                                                {
                                                                    label: "Solicitações",
                                                                    data: type === DashboardTypes.general ?
                                                                        requests?.requestRealizedMetrics?.map(item => item.count) :
                                                                        requests?.requestRealizedTeamMetrics?.map(item => item.count),
                                                                }
                                                            ],
                                                        },
                                                        siteVariables: globalTheme.siteVariables,
                                                        theme: theme,
                                                        stacked: false,
                                                        customColors: realizedRequestsColors,
                                                        activeSegmentIndex: activeRealizedRequestsIndex,
                                                        onChangeSegmentIndex: (index) => {
                                                            setActiveRealizedRequestsIndex(index);
                                                        }
                                                    }}
                                                />
                                            )
                                        }}
                                    />
                                </Flex>
                            </Flex>
                        </Flex>
                    </Segment>

                    {type === DashboardTypes.team &&
                        <Segment className="container">
                            <Flex className="leftPanel semilarge" gap="gap.medium" padding="padding.medium" column>
                                <Text content="Perfis de uso" size="medium" weight="semibold" />

                                <Flex className="chartContainer" gap="gap.smaller" column>
                                    <Flex className="chartUso" column>
                                        <FluentUIThemeConsumer
                                            render={(globalTheme) => {
                                                return (
                                                    <MemDoughnutChart
                                                        {...{
                                                            title: "Perfil de uso",
                                                            data: {
                                                                labels: usage?.usageMetrics?.map(item => item.label),
                                                                datasets: [
                                                                    {
                                                                        label: "",
                                                                        data: usage?.usageMetrics?.map(item => item.sum),
                                                                    }
                                                                ],
                                                            },
                                                            siteVariables: globalTheme.siteVariables,
                                                            theme: theme,
                                                            cutoutPercentage: 70,
                                                            borderWidth: 2,
                                                            customColors: usageColors,
                                                            collapsed: true,
                                                            custom: true,
                                                            activeSegmentIndex: activeUsageIndex,
                                                            onChangeSegmentIndex: (index) => {
                                                                setActiveUsageIndex(index);
                                                            }
                                                        }}
                                                    />
                                                )
                                            }}
                                        />
                                    </Flex>

                                    <Text className="descriptionHighlighted" content="Visão de solicitações atendidas agrupadas por perfil de uso (Geral, Magistrados e Plantão). Selecione um dos perfis para refletir na listagem abaixo." size="small" />
                                </Flex>
                            </Flex>
                            <Flex className="rightPanel" gap="gap.medium" padding="padding.medium" column>
                                <Flex gap="gap.smaller" column>
                                    <Text content="Uso por equipe de atendimento" size="medium" weight="semibold" />
                                    <Text className="description" content="Visão de solicitações atendidas agrupadas por perfil de uso e equipe de atendimento." size="small" />
                                </Flex>

                                <Flex id="chartContainerScrollable" className="chartContainerScrollable" gap="gap.smaller" column>
                                    <Flex id="chartEquipe" className="chartEquipe" gap="gap.small" column>

                                        <div style={{ height: transformUsageConsolidatedMetrics(usage?.usageConsolidatedMetrics).height }}>
                                            <FluentUIThemeConsumer
                                                render={(globalTheme) => {
                                                    return (
                                                        <MemBarHorizontalChart
                                                            {...{
                                                                title: "Uso por equipe de atendimento",
                                                                data: transformUsageConsolidatedMetrics(usage?.usageConsolidatedMetrics),
                                                                siteVariables: globalTheme.siteVariables,
                                                                theme: theme,
                                                                customColors: teamUsageColors,
                                                                customHeight: transformUsageConsolidatedMetrics(usage?.usageConsolidatedMetrics).height,
                                                                stacked: true,
                                                                layout: {
                                                                    top: 0,
                                                                    right: 10,
                                                                    left: -45,
                                                                },
                                                                mirror: true,
                                                                yLabelOffset: -20,
                                                                barPercentage: 0.6,
                                                                categoryPercentage: 0.5,
                                                                hideLegend: true,
                                                                activeSegmentIndex: activeTeamUsageIndex,
                                                                onChangeSegmentIndex: (index, datasetIndex) => {
                                                                    setActiveTeamUsageIndex([index, datasetIndex]);
                                                                }
                                                            }}
                                                        />
                                                    )
                                                }}
                                            />
                                        </div>
                                    </Flex>
                                </Flex>
                            </Flex>
                        </Segment>
                    }

                    <Segment className="container">
                        <Flex className="rightPanel full" gap="gap.medium" padding="padding.medium" column>
                            <Flex gap="gap.medium" column>
                                <Table
                                    className="chartTable chartTableUsage"
                                    header={{
                                        items: ['Protocolo', 'Data', 'Assunto', 'Solicitante', 'Atendente', 'Avaliação'],
                                    }}
                                    rows={realizedUsageList}
                                />

                                <div className="pagination">
                                    <Pagination
                                        callbackFromParent={async (pageNumber: number) => {
                                            setRealizedUsageListPageNumber(pageNumber);

                                            await getRealizedUsageListData(pageNumber * 10, chartFilters);
                                        }}
                                        entitiesLength={realizedUsageListCount}
                                        activePage={realizedUsageListPageNumber}
                                        numberOfContents={10}
                                    />
                                </div>
                            </Flex>
                        </Flex>
                    </Segment>
                </Flex>
        );
    }

    const renderUnidades = () => {
        return (
            loadingCharts ?
                <Loader />
                :
                <Flex gap="gap.medium" column>
                    <Segment className="container">
                        <Flex className="leftPanel" gap="gap.medium" padding="padding.medium" column>
                            {/*<Text className="title" content="Total de novas solicitações" size="medium" weight="semibold" />*/}

                            <Flex className="containerBorder" gap="gap.smaller" column>
                                <Text
                                    className="textHeight"
                                    content={new Intl.NumberFormat('pt-BR').format(requests?.totalCount)}
                                    size="larger"
                                    weight="bold"
                                />
                                <Text className="description" content="Total de novas solicitações" size="small" />
                            </Flex>

                            <Flex gap="gap.smaller" column>
                                <Flex className="containerBorder" style={{ borderColor: requestsStatusColors[0] }} gap="gap.smaller">
                                    {type === DashboardTypes.general ?
                                        <Text
                                            className="textHeight"
                                            content={new Intl.NumberFormat('pt-BR').format((requests?.requestsStatusTotalsMetrics?.find(item => item.value === statusQueueConstants.naFila)?.count ?? 0))}
                                            size="large"
                                            weight="bold"
                                        />
                                        :
                                        <Text
                                            className="textHeight"
                                            content={new Intl.NumberFormat('pt-BR').format((requests?.requestsTeamStatusTotalsMetrics?.find(item => item.value === statusQueueConstants.naFila)?.count ?? 0))}
                                            size="large"
                                            weight="bold"
                                        />
                                    }
                                    <Text className="description" content="Na fila" size="small" />
                                </Flex>

                                <Flex className="containerBorder" style={{ borderColor: requestsStatusColors[1] }} gap="gap.smaller">
                                    {type === DashboardTypes.general ?
                                        <Text
                                            className="textHeight"
                                            content={
                                                new Intl.NumberFormat('pt-BR').format(
                                                    ((requests?.requestsStatusTotalsMetrics?.find(item => item.value === statusQueueConstants.atendimentoIniciado)?.count ?? 0) +
                                                        (requests?.requestsStatusTotalsMetrics?.find(item => item.value === statusQueueConstants.emAtendimento)?.count ?? 0))
                                                )
                                            }
                                            size="large"
                                            weight="bold"
                                        />
                                        :
                                        <Text
                                            className="textHeight"
                                            content={
                                                new Intl.NumberFormat('pt-BR').format(
                                                    ((requests?.requestsTeamStatusTotalsMetrics?.find(item => item.value === statusQueueConstants.atendimentoIniciado)?.count ?? 0) +
                                                        (requests?.requestsTeamStatusTotalsMetrics?.find(item => item.value === statusQueueConstants.emAtendimento)?.count ?? 0))
                                                )
                                            }
                                            size="large"
                                            weight="bold"
                                        />
                                    }

                                    <Text className="description" content="Em atendimento" size="small" />
                                </Flex>

                                <Flex className="containerBorder" style={{ borderColor: requestsStatusColors[2] }} gap="gap.smaller">
                                    {type === DashboardTypes.general ?
                                        <Text
                                            className="textHeight"
                                            content={
                                                new Intl.NumberFormat('pt-BR').format(
                                                    ((requests?.requestsStatusTotalsMetrics?.find(item => item.value === statusQueueConstants.atendimentoEncerrado)?.count ?? 0) +
                                                        (requests?.requestsStatusTotalsMetrics?.find(item => item.value === statusQueueConstants.atendimentoCancelado)?.count ?? 0))
                                                )
                                            }
                                            size="large"
                                            weight="bold"
                                        />
                                        :
                                        <Text
                                            className="textHeight"
                                            content={
                                                new Intl.NumberFormat('pt-BR').format(
                                                    ((requests?.requestsTeamStatusTotalsMetrics?.find(item => item.value === statusQueueConstants.atendimentoEncerrado)?.count ?? 0) +
                                                        (requests?.requestsTeamStatusTotalsMetrics?.find(item => item.value === statusQueueConstants.atendimentoCancelado)?.count ?? 0))
                                                )
                                            }
                                            size="large"
                                            weight="bold"
                                        />
                                    }

                                    <Text className="description" content="Finalizado" size="small" />
                                </Flex>

                                {type === DashboardTypes.general &&
                                    <Flex className="containerBorder" style={{ borderColor: requestsStatusColors[3] }} gap="gap.smaller">
                                        <Text
                                            className="textHeight"
                                            content={
                                                new Intl.NumberFormat('pt-BR').format((requests?.requestsStatusTotalsMetrics?.find(item => item.value === statusQueueConstants.atendimentoInativo)?.count ?? 0))
                                            }
                                            size="large"
                                            weight="bold"
                                        />

                                        <Text className="description" content="Expirado" size="small" />
                                    </Flex>
                                }
                            </Flex>

                        </Flex>
                        <Flex className="rightPanel" gap="gap.medium" padding="padding.medium" column>
                            <Flex gap="gap.smaller" column>
                                <Text className="description" content="Resumo mensal de novas solicitações recebidas, agrupadas por status de atendimento." size="small" />
                            </Flex>

                            <Flex className="chartContainer" gap="gap.smaller" column>
                                <Flex className="chartSolicitacoes" column>
                                    <FluentUIThemeConsumer
                                        render={(globalTheme) => {
                                            return (
                                                <MemBarChart
                                                    {...{
                                                        title: "Unidades",
                                                        data: {
                                                            labels: type === DashboardTypes.general ?
                                                                requests?.requestMetrics?.map(item => getMonthName(item.dateString)) :
                                                                requests?.requestTeamMetrics?.map(item => getMonthName(item.dateString)),
                                                            datasets: type === DashboardTypes.general ? transformStatusRequestsConsolidatedMetrics(requests?.requestsStatusMetrics).datasets :
                                                                transformStatusRequestsConsolidatedMetrics(requests?.requestsTeamStatusMetrics).datasets,
                                                        },
                                                        siteVariables: globalTheme.siteVariables,
                                                        theme: theme,
                                                        customColors: requestsStatusColors.reverse(),
                                                        stacked: true,
                                                        customDataLabel: true,
                                                        activeSegmentIndex: activeRequestsStatusIndex,
                                                        onChangeSegmentIndex: (index, datasetIndex) => {
                                                            setActiveRequestsStatusIndex([index, datasetIndex]);
                                                        },
                                                        clickable: false,
                                                    }}
                                                />
                                            );
                                        }}
                                    />
                                </Flex>
                            </Flex>
                        </Flex>
                    </Segment>

                    {unitCategories?.length &&
                        <Segment className="container">
                            <Flex className="rightPanel rightPanelUnit full" gap="gap.medium" padding="padding.medium" column>
                                <Text className="textHeight" content="Categorias" size="medium" weight="semibold" />

                                <Flex gap="gap.medium" vAlign="center" style={{ rowGap: '1rem' }} wrap>
                                    {unitCategories.map(c =>
                                        <Text
                                            className={`tag actionTag ${selectedCategory === c ? 'selectedTag' : ''}`}
                                            content={c}
                                            size="medium"
                                            onClick={() => {
                                                if (selectedCategory === c) {
                                                    setMeetingDataList(originalMeetingDataList);
                                                    setSelectedCategory(null);
                                                } else {
                                                    const filtered = originalMeetingDataList.filter(m => m.categoria === c);
                                                    setMeetingDataList(filtered);
                                                    setSelectedCategory(c);
                                                }
                                            }}
                                        />
                                    )}
                                </Flex>
                            </Flex>
                        </Segment>
                    }

                    <Segment className="container">
                        <Flex className="rightPanel rightPanelUnit full" gap="gap.medium" padding="padding.medium" column>
                            <Flex className="unitTableWrapper" gap="gap.medium" column>
                                <Table
                                    className="chartTable chartTableUnit"
                                    header={{
                                        items: ['Unidade', 'Total de Solicitações', 'Total de Atendimentos', 'Percentual de Atendidos', 'Total de Atendentes', 'TME', 'TMA'],
                                    }}
                                    rows={meetingDataList.map((r, i) => ({
                                        key: i,
                                        items: [
                                            {
                                                key: `label-${i}`, content: (
                                                    <Flex gap="gap.smaller" vAlign="center" column>
                                                        <Flex gap="gap.smaller" vAlign="center">
                                                            <Text content={r.name} weight="semibold" />
                                                        </Flex>

                                                        {/*{r.categoria &&*/}
                                                        {/*    <Tooltip key={`categoria`} content={`Categoria`} position="after" align="center">*/}
                                                        {/*        <Text className="tag customTag" content={r.categoria} />*/}
                                                        {/*    </Tooltip>*/}
                                                        {/*}*/}

                                                        {r.metadata && Object.keys(r.metadata).length > 0 &&
                                                            <Flex gap="gap.smaller" vAlign="center">
                                                                {Object.entries(r.metadata).map(([key, value], index) => (
                                                                    <Tooltip key={`${key}-${index}`} content={key} position="after" align="center">
                                                                        <Text className="tag" content={value} />
                                                                    </Tooltip>
                                                                ))}
                                                            </Flex>
                                                        }
                                                    </Flex>
                                                )
                                            },
                                            { key: `sum-${i}`, content: new Intl.NumberFormat('pt-BR').format(r.sum) },
                                            { key: `realized-${i}`, content: new Intl.NumberFormat('pt-BR').format(r.realizedSum) },
                                            { key: `percent-${i}`, content: `${r.percent}%` },
                                            { key: `attendants-${i}`, content: r.totalAtendentes },
                                            { key: `tme-${i}`, content: `${formatTime(r.tempoMedioEsperaMin)}` },
                                            { key: `tma-${i}`, content: `${formatTime(r.tempoMedioAtendimentoMin)}` }
                                        ],
                                        onClick: async () => {
                                            setChartFilters({
                                                chartType: ChartTypes.unitChart,
                                                index: i,
                                                value: r.label,
                                                additional: r.name,
                                            });
                                        },
                                        styles: chartFilters && chartFilters.index === i ? { cursor: "pointer", backgroundColor: theme === "dark" ? "rgb(59, 58, 57)" : "rgb(237, 235, 233)" } : { cursor: "pointer" }
                                    }))}
                                    aria-label="Static table"
                                />
                            </Flex>
                        </Flex>
                    </Segment>
                </Flex>
        );
    }

    const renderAvaliacoes = () => {
        return (
            loadingCharts ?
                <Loader />
                :
                <Flex gap="gap.medium" column>
                    <Segment className="container">
                        <Flex className="leftPanel large" gap="gap.medium" padding="padding.medium" column>
                            {/*<Text className="title" content="Visão geral" size="medium" weight="semibold" />*/}
                            {(rating?.csat && typeof (rating?.csat) === "number" || rating?.csatTeam && typeof (rating?.csatTeam) === "number") &&
                                <Flex className="ratingContainerBorder" gap="gap.smaller" column>
                                    <Text
                                        className="textHeight"
                                        content={rating?.csat ? rating?.csat.toFixed(2) + '%' : rating?.csatTeam.toFixed(2) + '%'}
                                        size="larger"
                                        weight="bold"
                                    />

                                    <Text className="description" content="CSAT - Índice de Satisfação do Cliente" size="small" />
                                </Flex>
                            }

                            <Flex className="ratingContainerBorder" gap="gap.smaller" column>
                                <Text
                                    className="textHeight"
                                    content={`${new Intl.NumberFormat('pt-BR').format(rating?.totalCount)} / ${new Intl.NumberFormat('pt-BR').format(realizedRequests.realizedTotalCount)}`}
                                    size="larger"
                                    weight="bold"
                                />

                                <Text className="description" content="Número de avaliações / Número de atendimentos" size="small" />
                            </Flex>

                            <Text className="descriptionHighlighted" content="CSAT é o termômetro que mede o nível de satisfação do solicitante com o atendimento recebido, utilizando as notas de 1 a 5 como referência." size="small" />
                        </Flex>

                        <Flex className="rightPanel" gap="gap.medium" padding="padding.medium" column>
                            {/*<Text content="Avaliações de atendimento" size="medium" weight="semibold" />*/}
                            <Text className="description" content="Resumo de avaliações de atendimento." size="small" />

                            <Flex className="chartContainer" gap="gap.smaller" column>
                                <Flex className="chartRating" column>
                                    <FluentUIThemeConsumer
                                        render={(globalTheme) => {
                                            return (
                                                <MemBarHorizontalRatingChart
                                                    {...{
                                                        title: "Número de avaliações",
                                                        data: {
                                                            labels: type === DashboardTypes.team ? [
                                                                rating?.ratingTeamMetrics[0].value?.toString() + " - Ótimo",
                                                                rating?.ratingTeamMetrics[1].value?.toString() + " - Bom",
                                                                rating?.ratingTeamMetrics[2].value?.toString() + " - Regular",
                                                                rating?.ratingTeamMetrics[3].value?.toString() + " - Insuficiente",
                                                                rating?.ratingTeamMetrics[4].value?.toString() + " - Fraco"
                                                            ] : [
                                                                rating?.ratingMetrics[0].value?.toString() + " - Ótimo",
                                                                rating?.ratingMetrics[1].value?.toString() + " - Bom",
                                                                rating?.ratingMetrics[2].value?.toString() + " - Regular",
                                                                rating?.ratingMetrics[3].value?.toString() + " - Insuficiente",
                                                                rating?.ratingMetrics[4].value?.toString() + " - Fraco"
                                                            ],
                                                            datasets: [
                                                                {
                                                                    label: " ",
                                                                    data: type === DashboardTypes.team ? [
                                                                        rating?.ratingTeamMetrics[0].count,
                                                                        rating?.ratingTeamMetrics[1].count,
                                                                        rating?.ratingTeamMetrics[2].count,
                                                                        rating?.ratingTeamMetrics[3].count,
                                                                        rating?.ratingTeamMetrics[4].count
                                                                    ] : [
                                                                        rating?.ratingMetrics[0].count,
                                                                        rating?.ratingMetrics[1].count,
                                                                        rating?.ratingMetrics[2].count,
                                                                        rating?.ratingMetrics[3].count,
                                                                        rating?.ratingMetrics[4].count
                                                                    ],
                                                                }
                                                            ],
                                                        },
                                                        siteVariables: globalTheme.siteVariables,
                                                        theme: theme,
                                                        customColors: ratingColors,
                                                        stacked: false,
                                                        layout: {
                                                            top: 0,
                                                            right: 0,
                                                            bottom: 0,
                                                            left: 0,
                                                        },
                                                        mirror: false,
                                                        barPercentage: 0.8,
                                                        categoryPercentage: 1,
                                                        fillBarLength: true,
                                                        ticksMax: rating?.totalCount,
                                                        backgroundBar: true,
                                                        textBar: true,
                                                        gradientBar: false,
                                                        hideLegend: true,
                                                        customTooltip: true,
                                                        activeSegmentIndex: activeRatingIndex,
                                                        onChangeSegmentIndex: (index) => {
                                                            setActiveRatingIndex(index);
                                                        }
                                                    }}
                                                />
                                            )
                                        }}
                                    />
                                </Flex>
                            </Flex>
                        </Flex>
                    </Segment>

                    {type === DashboardTypes.team &&
                        <Segment className="container">
                            <Flex className="rightPanel full" gap="gap.medium" padding="padding.medium" column>
                                <Flex gap="gap.smaller" column>
                                    <Text content="Avaliações por equipe de atendimento" size="medium" weight="semibold" />
                                    <Text className="description" content="Visão de avaliações agrupadas por equipe de atendimento." size="small" />
                                </Flex>

                                <Flex className="chartContainer list" gap="gap.smaller" column>
                                    <Flex id="chartTeamRating" className="chartTeamRating" column>
                                        <div style={{ height: transformRatingConsolidatedMetrics(rating?.ratingUsageConsolidatedMetrics).height }}>
                                            <FluentUIThemeConsumer
                                                render={(globalTheme) => {
                                                    return (
                                                        <MemBarHorizontalRatingTeamChart
                                                            {...{
                                                                title: "Número de avaliações",
                                                                data: transformRatingConsolidatedMetrics(rating?.ratingUsageConsolidatedMetrics),
                                                                siteVariables: globalTheme.siteVariables,
                                                                theme: theme,
                                                                customColors: ["#ff8c5a", "#ffb234", "#ffd934", "#add633", "#a0c15a"],
                                                                customHeight: transformRatingConsolidatedMetrics(rating?.ratingUsageConsolidatedMetrics).height,
                                                                stacked: true,
                                                                layout: {
                                                                    top: 0,
                                                                    right: 10,
                                                                    left: -45,
                                                                },
                                                                mirror: true,
                                                                yLabelOffset: -20,
                                                                barPercentage: 0.6,
                                                                categoryPercentage: 0.5,
                                                                fillBarLength: true,
                                                                ticksMax: 100,
                                                                backgroundBar: false,
                                                                textBar: false,
                                                                gradientBar: false,
                                                                hideLegend: false,
                                                                barPercent: true,
                                                                //customLegend: true,
                                                                activeSegmentIndex: activeTeamRatingIndex,
                                                                onChangeSegmentIndex: (index, datasetIndex) => {
                                                                    setActiveTeamRatingIndex([index, datasetIndex]);
                                                                }
                                                            }}
                                                        />
                                                    )
                                                }}
                                            />
                                        </div>
                                    </Flex>
                                </Flex>
                            </Flex>
                        </Segment>
                    }

                    <Segment className="container">
                        <Flex className="rightPanel full" gap="gap.medium" padding="padding.medium" column>
                            <Flex gap="gap.medium" column>
                                <Table
                                    className="chartTable chartTableRating"
                                    header={{
                                        items: ['Protocolo', 'Data', 'Assunto', 'Solicitante', 'Atendente', 'Avaliação', 'Comentário'],
                                    }}
                                    rows={ratingList}
                                    aria-label="Static table"
                                />

                                <div className="pagination">
                                    <Pagination
                                        callbackFromParent={async (pageNumber: number) => {
                                            setRatingListPageNumber(pageNumber);

                                            await getRatingListData(pageNumber * 10, chartFilters);
                                        }}
                                        entitiesLength={ratingListCount}
                                        activePage={ratingListPageNumber}
                                        numberOfContents={10}
                                    />
                                </div>
                            </Flex>
                        </Flex>
                    </Segment>
                </Flex>
        );
    }

    return (
        <>
            {loading ?
                <div className="dashboardLoaderContainer">
                    <Loader />
                </div>
                :
                <div className="container">
                    <Flex className="headerContainer" vAlign="center" space="between">
                        <Menu
                            className="menu"
                            items={[
                                {
                                    key: 'geral',
                                    content: 'Visão Geral',
                                },
                                {
                                    key: 'solicitacoes',
                                    content: 'Solicitações',
                                },
                                {
                                    key: 'atendimentos',
                                    content: 'Atendimentos',
                                },
                                {
                                    key: 'unidades',
                                    content: 'Unidades',
                                    disabled: type === DashboardTypes.team,
                                },
                                {
                                    key: 'avaliacoes',
                                    content: 'Avaliações',
                                },
                            ]}
                            onItemClick={sessionChange}
                            activeIndex={menuIndex}
                            underlined
                            primary
                            accessibility={tabListBehavior}
                        />

                        {chartFilters != null &&
                            <>
                                {(chartFilters.chartType === 1 && menuIndex === 0) &&
                                    <Pill className="filter">
                                        <Flex vAlign="center">
                                            <span className="box" style={{ backgroundColor: requestsColors[chartFilters.index[1]] }}></span>
                                            {activeOverviewIndex[1] === 0 ?
                                                <span>{ChartTypes.requestsLabel}</span>
                                                :
                                                <span>{ChartTypes.realizedRequestsLabel}</span>
                                            }
                                        </Flex>
                                    </Pill>
                                }
                                {(chartFilters.chartType === 1 && menuIndex === 1) &&
                                    <Pill className="filter">
                                        <Flex vAlign="center">
                                            <span className="box" style={{ backgroundColor: requestsColors[0] }}></span>
                                            <span>{ChartTypes.requestsLabel}</span>
                                        </Flex>
                                    </Pill>
                                }
                                {chartFilters.chartType === 2 &&
                                    <Pill className="filter">
                                        <Flex vAlign="center">
                                            <span>{ChartTypes.usageLabel}:</span>
                                            <span className="box" style={{ backgroundColor: usageColors[activeUsageIndex] }}></span>
                                            <span>{chartFilters.value}</span>
                                        </Flex>
                                    </Pill>
                                }
                                {chartFilters.chartType === 3 &&
                                    <Flex vAlign="center">
                                        <Pill className="filter">
                                            <Flex vAlign="center">
                                                <span>{ChartTypes.teamUsageLabel}:</span>
                                                <span>{transformUsageConsolidatedMetrics(usage?.usageConsolidatedMetrics).labels[chartFilters.index[0]]}</span>
                                            </Flex>
                                        </Pill>
                                        <Pill className="filter">
                                            <Flex vAlign="center">
                                                <span className="box" style={{ backgroundColor: teamUsageColors[chartFilters.index[1]] }}></span>
                                                <span>{chartFilters.additional}</span>
                                            </Flex>
                                        </Pill>
                                    </Flex>
                                }
                                {chartFilters.chartType === 4 &&
                                    <Pill className="filter">
                                        <Flex vAlign="center">
                                            <span>{ChartTypes.ratingLabel}:</span>
                                            <span className="box" style={{ backgroundColor: ratingColors[activeRatingIndex] }}></span>
                                            <span>{formatRatingLabel(Number(chartFilters.value))}</span>
                                        </Flex>
                                    </Pill>
                                }
                                {chartFilters.chartType === 5 &&
                                    <Flex vAlign="center">
                                        <Pill className="filter">
                                            <Flex vAlign="center">
                                                <span>{ChartTypes.ratingTeamLabel}:</span>
                                                <span>{transformRatingConsolidatedMetrics(rating?.ratingUsageConsolidatedMetrics).labels[chartFilters.index[0]]}</span>
                                            </Flex>
                                        </Pill>
                                        <Pill className="filter">
                                            <Flex vAlign="center">
                                                <span className="box" style={{ backgroundColor: ratingColors.reverse()[chartFilters.index[1]] }}></span>
                                                <span>{formatRatingLabel(Number(chartFilters.additional))}</span>
                                            </Flex>
                                        </Pill>
                                    </Flex>
                                }
                                {(chartFilters.chartType === 6 && menuIndex === 0) &&
                                    <Pill className="filter">
                                        <Flex vAlign="center">
                                            <span className="box" style={{ backgroundColor: requestsColors[chartFilters.index[1]] }}></span>
                                            <span>{ChartTypes.realizedRequestsLabel}</span>
                                        </Flex>
                                    </Pill>
                                }
                                {(chartFilters.chartType === 6 && menuIndex === 2) &&
                                    <Pill className="filter">
                                        <Flex vAlign="center">
                                            <span className="box" style={{ backgroundColor: realizedRequestsColors[0] }}></span>
                                            <span>{ChartTypes.realizedRequestsLabel}</span>
                                        </Flex>
                                    </Pill>
                                }
                                {chartFilters.chartType === 7 &&
                                    <Flex vAlign="center">
                                        <Pill className="filter">
                                            <Flex vAlign="center">
                                                <span>{ChartTypes.requestsStatusLabel}:</span>
                                                <span className="box" style={{ backgroundColor: requestsStatusColorsReversed[chartFilters.index[1]] }}></span>
                                                <span>{chartFilters.additional}</span>
                                            </Flex>
                                        </Pill>
                                    </Flex>
                                }
                                {(chartFilters.chartType === 8 && menuIndex === 3) &&
                                    <Pill className="filter">
                                        <Flex vAlign="center">
                                            <span className="box" style={{ backgroundColor: requestsColors[0] }}></span>
                                            <span>{ChartTypes.unitLabel}:</span>
                                            <span>{chartFilters.additional}</span>
                                        </Flex>
                                    </Pill>
                                }
                            </>
                        }

                        <Flex className="dateFilter" gap="gap.smaller" vAlign="center">
                            <CalendarIcon outline />
                            {chartFilters != null && chartFilters.chartType === 1 ?
                                <Text content={`Período: ${formatDateString(chartFilters.value)}`} />
                                : chartFilters != null && chartFilters.chartType === 6 ?
                                    <Text content={`Período: ${formatDateString(chartFilters.value)}`} />
                                    : chartFilters != null && chartFilters.chartType === 7 ?
                                        <Text content={`Período: ${formatDateString(chartFilters.value)}`} />
                                        :
                                        <Text content="Período: Últimos 6 meses" />
                            }
                        </Flex>
                    </Flex>
                    <Flex className="dashboardContainer" gap="gap.large" column>
                        <Flex gap="gap.smaller" space="between">
                            <FlexItem grow>
                                <div className="wrapper">
                                    {menuIndex === 0 && renderVisaoGeral()}
                                    {menuIndex === 1 && renderSolicitacoes()}
                                    {menuIndex === 2 && renderAtendimentos()}
                                    {menuIndex === 3 && renderUnidades()}
                                    {menuIndex === 4 && renderAvaliacoes()}
                                </div>
                            </FlexItem>
                        </Flex>
                    </Flex>
                </div>
            }
        </>
    );
}

export default Dashboards;